import type { TeamFragmentFragment } from '@apps/www/src/__generated__/graphql';
import SVFeedLayoutToggleContainer from '@apps/www/src/www/containers/SVFeedLayoutToggleContainer';
import useAuthHasUserClass from '@apps/www/src/www/hooks/useAuthHasUserClass';
import useAuthTeam from '@apps/www/src/www/hooks/useAuthTeam';
import useIsTeamSideMenuRendered from '@apps/www/src/www/hooks/useIsTeamSideMenuRendered';
import useTeamHomePath from '@apps/www/src/www/hooks/useTeamHomePath';
import SVLink from '@pkgs/shared-client/components/SVLink';
import SVPageMargins from '@pkgs/shared-client/components/SVPageMargins';
import cn from '@pkgs/shared-client/helpers/cn';
import matchLocation from '@pkgs/shared-client/helpers/matchLocation';
import UserClass from '@pkgs/shared/enums/UserClass';
import type { NextRouter } from 'next/router';
import { useRouter } from 'next/router';

type Item = {
	to: string;
	label: string;
	matchUrl?: string;
	target?: string;
};

const isTeamPage = (router: NextRouter, team: TeamFragmentFragment) =>
	router.pathname.includes('/team') || (team.teamFeedAsHomepage && router.pathname === '/');

const SVFeedCTAs = () => {
	const router = useRouter();
	const team = useAuthTeam();
	const teamHomePath = useTeamHomePath();
	const isModerator = useAuthHasUserClass(UserClass.MODERATOR);
	const isSideMenuRendered = useIsTeamSideMenuRendered();
	const isTeamPageResult = team && isTeamPage(router, team);

	const items: Item[] = [
		{
			to: team?.teamFeedAsHomepage ? '/feed/' : '/',
			label: 'Feed',
		},
		...(team
			? [
					{
						to: teamHomePath,
						label: 'Team',
						matchUrl: '/team/**',
					},
			  ]
			: []),
		{ to: '/course/', label: 'Learn', matchUrl: '/course/**' },
	];

	return (
		<SVPageMargins className="-sm:flex hidden">
			<div className={cn('w-full', isTeamPageResult && isSideMenuRendered && '-mt-40 mb-24')}>
				<div
					className={cn(
						'h-full w-full py-6',
						isTeamPageResult && isModerator
							? 'flex justify-between'
							: 'flex-center space-x-7',
					)}
				>
					<div
						className={cn(
							'flex-center',
							isTeamPageResult && isSideMenuRendered ? 'space-x-5' : 'space-x-7',
						)}
					>
						{items.map((item) => (
							<SVLink
								key={item.to}
								to={item.to}
								className={cn(
									'max-w-[30vw] truncate text-lg font-medium',
									matchLocation(router.asPath, String(item.to)) ||
										(item.matchUrl &&
											matchLocation(router.asPath, String(item.matchUrl)))
										? 'text-primary'
										: 'text-gray-500',
								)}
							>
								{item.label}
							</SVLink>
						))}
					</div>
					{isModerator && isTeamPageResult && <SVFeedLayoutToggleContainer isShortMode />}
				</div>
			</div>
		</SVPageMargins>
	);
};

export default SVFeedCTAs;
